import { projector, renderCurves } from './research-wireframes.js';

// A schematic of image retrieval and pair curation, not real embedding coordinates.
const line = points => ({ points });
const rect = (x, y, w, h, z = 0) => [[x,y,z],[x+w,y,z],[x+w,y+h,z],[x,y+h,z],[x,y,z]];
const clamp = value => Math.max(0, Math.min(1, value));
const ease = value => { const t = clamp(value); return t*t*(3-2*t); };
const mix = (a, b, t) => a.map((v, i) => v + (b[i]-v)*t);
const shift = (curves, at, scale = 1) => curves.map(({points}) => line(points.map(p => p.map((v, i) => at[i]+v*scale))));

function imageCard(variant = 0) {
    const curves = [line(rect(-28,-22,56,44)), line([[-24,15,0],[-7,-5,0],[3,7,0],[12,-2,0],[24,15,0]])];
    const circle = [];
    for (let i=0;i<=16;i++) {
        const angle = i*Math.PI/8;
        circle.push([13+Math.cos(angle)*5,-11+Math.sin(angle)*5,0]);
    }
    curves.push(line(circle));
    if (variant) curves.push(line([[-22,-10,0],[-14,-10,0],[-10,-14,0],[-4,-10,0]]));
    return curves;
}

// A complete static assessment for reduced motion; the live loop moves one pair
// through search, assessment, and collection, with no simultaneous competing flow.
export function editPairState(seconds, reduced = false) {
    const phase = reduced ? 12 : ((seconds % 22)+22)%22;
    return {
        phase,
        gather: ease((phase-4)/4),
        review: clamp((phase-8)/4),
        deliver: ease((phase-13)/5),
        presence: reduced ? 1 : ease((phase-2.5)/1)*(1-ease((phase-21)/1)),
    };
}

// Caption tokens accumulate from a fixed left edge, one line after the other.
// Start just after the scan begins and finish before the assessment checkmark.
export function editPairCaption(review) {
    return [[60,0,.1,.55,18],[23,10,.55,.95,13]].flatMap(([right,y,start,end,tokens]) => {
        const revealed = Math.floor(clamp((review-start)/(end-start))*tokens)/tokens;
        return revealed > 0 ? [line([[-65,y,0],[-65+(right+65)*revealed,y,0]])] : [];
    });
}

// Rotate the cloud about its own center; cards stay camera-facing so their
// silhouettes remain readable throughout a full revolution.
export function editCloudPoint(point, center, seconds, reduced = false) {
    const angle = reduced ? .3 : seconds * .055 + .3;
    const yaw = Math.cos(angle), turn = Math.sin(angle);
    const pitch = .18 * Math.sin(angle * .7);
    const x = point[0] - center[0], y = point[1] - center[1], z = point[2] - center[2];
    const xx = x * yaw + z * turn, zz = -x * turn + z * yaw;
    return [center[0] + xx, center[1] + y * Math.cos(pitch) - zz * Math.sin(pitch),
        center[2] + y * Math.sin(pitch) + zz * Math.cos(pitch)];
}

// Freeze the departure point at pickup, so ongoing cloud rotation cannot tug
// the extracted pair sideways on its journey into the foreground review tray.
export function editPairPosition(point, center, target, destination, seconds, reduced = false) {
    const state = editPairState(seconds, reduced);
    const departureTime = reduced ? 0 : seconds - Math.max(0, state.phase - 4);
    const start = editCloudPoint(point, center, departureTime, reduced);
    return mix(mix(start, target, state.gather), destination, state.deliver);
}

function geometry(small) {
    const cloud = small ? [-55,-150,210] : [-420,0,240];
    const review = small ? [-135,145,-125] : [50,0,-150];
    const output = small ? [170,145,15] : [320,0,15];
    const cards = [imageCard(), imageCard(1)];
    const curves = [], positions = [], cloudCards = [];
    // Many individual image embeddings, spread through a 3D volume. Coordinates
    // are illustrative; nearest-neighbor selection uses their actual distances.
    for (let i=0;i<96;i++) {
        const angle=i*2.39996, radius=(small?28:38)+Math.sqrt(i/95)*(small?204:218);
        const at=[cloud[0]+Math.cos(angle)*radius,cloud[1]+Math.sin(angle)*radius*(small?.66:.82),cloud[2]+Math.sin(i*1.7)*(small?115:150)];
        positions.push(at);
        cloudCards.push({card:cards[i%2], at, size:.32+(Math.sin(i*2.1)+1)*.09});
    }
    const anchors=[[-55,-30,18],[52,24,-20],[-42,42,25],[33,-40,-24]]
        .map(p=>p.map((value,i)=>value+cloud[i]));
    const selections=anchors.map(anchor=>positions.map((at,index)=>({at,index,distance:Math.hypot(...at.map((v,i)=>v-anchor[i]))}))
        .sort((a,b)=>a.distance-b.distance).slice(0,2));
    // Paired-image review tray and a caption band, then a stack of training pairs.
    curves.push(...shift([line(rect(-125,-83,250,178)),line([[-110,48,0],[110,48,0]])], review));
    for (let layer=2;layer>=0;layer--) {
        const at = [output[0]+layer*7,output[1]-layer*9,output[2]+layer*16];
        curves.push(...shift([line(rect(-96,-65,192,146)),line([[0,-53,0],[0,38,0]]),line([[-80,60,0],[66,60,0]])],at));
        for (let i=0;i<2;i++) curves.push(...shift(cards[i],[at[0]+(i?48:-48),at[1]-8,at[2]],1.05));
    }
    const route = [
        [cloud[0]+(small?0:174),cloud[1]+(small?125:0),cloud[2]],
        [review[0]-(small?0:139),review[1]-(small?100:0),review[2]],
    ];
    curves.push(line(route),line([[review[0]+139,review[1],review[2]],[output[0]-115,output[1],output[2]]]));
    const target = i => [review[0]+(i?59:-59),review[1]-12,review[2]-5];
    const destination = i => [output[0]+(i?48:-48),output[1]-8,output[2]-5];
    const selected = cards.map(card => [...card, line(rect(-33,-27,66,54))]);
    return {curves,cloud,cloudCards,selected,selections,anchors,review,target,destination};
}

export function createEditPairScene() {
    const models = [geometry(false), geometry(true)];
    const sphere=[];
    for(let axis=0;axis<3;axis++) {
        const points=[];
        for(let i=0;i<=48;i++) {
            const a=i*Math.PI/24,p=[Math.cos(a)*58,Math.sin(a)*58,0];
            points.push(axis===0?p:axis===1?[p[0],0,p[1]]:[0,p[0],p[1]]);
        }
        sphere.push(line(points));
    }
    const scan = [line([[-110,0,0],[110,0,0]])];
    const check = [line([[0,0,0],[6,7,0],[18,-9,0]])];
    let previousKey, previous;
    return {labels: [], draw(seconds, small, reduced = false) {
        const time = reduced ? 0 : Math.floor(seconds*(small?24:30))/(small?24:30);
        const key = `${time}/${small}/${reduced}`;
        if (key===previousKey) return previous;
        const model=models[+small], state=editPairState(time,reduced);
        // Keep the camera fixed: depth comes from the cloud's rotation and the
        // pair moving from distant embeddings to the foreground review tray.
        const project=projector({time:0,phase:1.2,x:small?380:600,y:small?285:230,scale:small?.94:1.04,depth:.8});
        let path=renderCurves(model.curves,project,0,true);
        const drawAt=(curves,at,scale=1) => renderCurves(curves,p=>project(p.map((v,i)=>at[i]+v*scale)),0,true);
        const cloudPoint=point=>editCloudPoint(point,model.cloud,time,reduced);
        for (const {card,at,size} of model.cloudCards) path+=drawAt(card,cloudPoint(at),size);
        const cycle=reduced?0:Math.floor(time/22)%model.anchors.length;
        const selection=model.selections[cycle];
        const searchProgress=reduced?1:ease(state.phase/3);
        const query=cloudPoint(mix(model.anchors[(cycle+3)%4],model.anchors[cycle],searchProgress));
        // The search volume expands enough to include both retrieved neighbors.
        const previousRadius=Math.max(58,model.selections[(cycle+3)%4][1].distance+8);
        const nextRadius=Math.max(58,selection[1].distance+8);
        path+=drawAt(sphere,query,(previousRadius+(nextRadius-previousRadius)*searchProgress)/58);
        for (let i=0;i<2;i++) {
            const at=editPairPosition(selection[i].at,model.cloud,model.target(i),model.destination(i),time,reduced);
            const size=(.8+.85*state.gather-.6*state.deliver)*state.presence;
            if (size>.01) path+=drawAt(model.selected[selection[i].index%2],at,size);
        }
        if ((state.phase>=3 && state.phase<8) || reduced) {
            for (const candidate of selection) {
                const end=mix(query,cloudPoint(candidate.at),reduced?1:ease(state.phase-3));
                const a=project(query),b=project(end);
                path+=`M${a[0].toFixed(1)},${a[1].toFixed(1)}L${b[0].toFixed(1)},${b[1].toFixed(1)}`;
            }
        }
        if (state.review>0) {
            const r=model.review;
            if (state.phase<12) path+=drawAt(scan,[r[0],r[1]-60+state.review*96,r[2]-9]);
            path+=drawAt(editPairCaption(state.review),[r[0],r[1]+65,r[2]-9],1.25);
            if (state.review===1) path+=drawAt(check,[r[0]+93,r[1]-64,r[2]-9]);
        }
        previousKey=key;
        previous={path,labels:[]};
        return previous;
    }};
}
